/**
 * The single money formatter for the whole portal.
 *
 * Emits the string the approved mocks specify, exactly:
 *
 *     R 12 500.00
 *
 * - "R" then a plain U+0020 space
 * - thousands grouped with a plain U+0020 space
 * - a dot decimal separator
 * - always two decimal places
 *
 * Why not `toLocaleString("en-ZA")`, which the app used in six places:
 * en-ZA emits `12 500,00` — a COMMA decimal and a U+00A0 NO-BREAK SPACE
 * group separator. Both are wrong against the mocks, which use U+002E and
 * U+0020. Verified in Node, not assumed.
 *
 * Why not `toFixed(2)` + a grouping regex, which two modules hand-rolled:
 * `toFixed` switches to exponential notation at 1e21, so a corrupt amount
 * would render as "R 1e+21". `Intl` keeps grouping all the way up.
 *
 * en-US is hardcoded on purpose. It is not the user's locale — it is simply
 * the locale whose grouping (",") and decimal (".") are both ASCII, so the
 * only transform needed is comma -> space. The user's own locale must never
 * reach this function or the same amount would render differently on
 * different machines.
 *
 * Presentation only. This never rounds a stored value, only what is drawn.
 */
export const formatR = (value?: number | string | null): string => {
  const parsed = typeof value === "number" ? value : Number(value);
  // null, undefined, "", NaN and +/-Infinity all render as R 0.00, matching
  // the guards the nine copied formatters already had.
  let amount = Number.isFinite(parsed) ? parsed : 0;
  // -0 formats as "-0.00"; a rounded-away negative should not print a sign.
  if (amount === 0) amount = 0;

  // Negatives keep the sign inside the amount — "R -450.00" — which is what
  // the app already renders today. The mocks do not specify a negative, so
  // this pass does not invent "-R 450.00".
  return (
    "R " +
    amount
      .toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      .replace(/,/g, " ")
  );
};
